import { useState } from 'react';
import { useInviteMember } from '../../hooks/useOrganizations';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import type { OrganizationRole } from '../../types/organization';
import { ROLE_HIERARCHY } from '../../types/organization';
import './CreateOrganizationModal.css'; // Reuse modal styles

interface InviteMemberModalProps {
  orgId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ALL_ROLES: OrganizationRole[] = ['viewer', 'member', 'admin', 'owner'];

export function InviteMemberModal({
  orgId,
  onClose,
  onSuccess,
}: InviteMemberModalProps) {
  const { currentRole } = useCurrentOrganization();
  const inviteMember = useInviteMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationRole>('member');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Filter available roles based on current user's role
  const availableRoles = ALL_ROLES.filter((r) => {
    if (!currentRole) return false;
    // Can only invite roles lower than your own
    return ROLE_HIERARCHY[r] < ROLE_HIERARCHY[currentRole];
  });

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || emailError) return;

    try {
      await inviteMember.mutateAsync({
        orgId,
        data: { email: email.trim().toLowerCase(), role },
      });
      onSuccess();
    } catch {
      // Error is handled by mutation
    }
  };

  const canSubmit =
    email.trim() && !emailError && !inviteMember.isPending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Invite Member</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="colleague@company.com"
                autoFocus
                required
              />
              {emailError && <p className="form-error">{emailError}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as OrganizationRole)}
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
              <p className="role-description">
                {role === 'viewer' && 'Can view organization data but cannot make changes.'}
                {role === 'member' && 'Can view and edit data within the organization.'}
                {role === 'admin' && 'Can manage members and organization settings.'}
                {role === 'owner' && 'Full access including deleting the organization.'}
              </p>
            </div>

            {inviteMember.isError && (
              <p className="form-error">
                {inviteMember.error?.message || 'Failed to send invitation'}
              </p>
            )}
          </div>

          <footer className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={inviteMember.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit}
            >
              {inviteMember.isPending ? 'Sending...' : 'Send Invitation'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
